// src/components/Sidebar/SidebarFrame.jsx
// ─────────────────────────────────────────────────────────────────────────────
// BoneFrame modülüne özgü sidebar.
// MOBİL DÜZELTMESİ: transform inline style objesinde tutulur.
//
// Z-Index hiyerarşisi:  hamburger 10001 · overlay 10000 · drawer 9999
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect } from "react";
import { useNavigate }      from "react-router-dom";
import { useTranslation }   from "react-i18next";
import { useSidebarFrame }  from "../../context/SidebarFrameContext.jsx";
import { useAuth }          from "../../context/AuthContext.jsx";
import { useAdmin }         from "../../context/AdminContext.jsx";
import { logout }           from "../../firebase/auth.js";

const C = {
  bg:           "#0b0b14",
  bgDeep:       "#060610",
  border:       "rgba(192,204,224,0.4)",
  borderHov:    "rgba(192,204,224,0.6)",
  silver:       "#c0cce0",
  silverDim:    "rgba(192,204,224,0.5)",
  silverGhost:  "var(--bone-text-muted)",
  silverFaint:  "rgba(192,204,224,0.08)",
  platinum:     "#e8eef7",
  platinumDim:  "rgba(232,238,247,0.6)",
  platinumGhost:"var(--bone-text-muted)",
  accent:       "#7ba7d4",
  accentDim:    "rgba(123,167,212,0.45)",
  red:          "#e05c6e",
  redDim:       "rgba(224,92,110,0.5)",
  SIDEBAR_W:    260,
};

function SilverRule({ opacity = 0.12 }) {
  return (
    <div style={{
      height: 1, margin: "4px 20px",
      background: `linear-gradient(90deg,transparent,rgba(192,204,224,${opacity}),transparent)`,
    }} />
  );
}

function NavItem({ itemKey, activeView, label, sub, icon, onClick }) {
  const active = activeView === itemKey;
  const [hov, setHov] = React.useState(false);
  const lit = active || hov;
  return (
    <button onClick={() => onClick(itemKey)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 14,
        padding: "12px 20px", position: "relative",
        background: active ? "rgba(192,204,224,0.07)" : hov ? "rgba(192,204,224,0.04)" : "transparent",
        border: "none", borderLeft: `2px solid ${active ? C.silver : "transparent"}`,
        cursor: "pointer", transition: "all 0.3s ease",
      }}
    >
      <span style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(90deg,rgba(192,204,224,0.05),transparent)", opacity: lit ? 1 : 0, transition: "opacity 0.3s ease" }} />
      <span style={{ width: 1, height: 28, flexShrink: 0, background: `rgba(192,204,224,${lit ? 0.3 : 0.1})`, transition: "background 0.3s ease" }} />
      <span style={{ color: lit ? C.silverDim : "rgba(192,204,224,0.22)", display: "flex", flexShrink: 0, transition: "color 0.3s ease" }}>{icon}</span>
      <span style={{ flex: 1, textAlign: "left" }}>
        <span style={{ display: "block", fontFamily: "'Courier Prime',monospace", fontSize: 12, letterSpacing: "0.12em", marginBottom: 2, color: active ? C.platinum : hov ? C.platinumDim : C.platinumGhost, transition: "color 0.3s ease" }}>{label}</span>
        <span style={{ display: "block", fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: lit ? "var(--bone-text-accent)" : "var(--bone-text-muted)", transition: "color 0.3s ease" }}>{sub}</span>
      </span>
      {active && <span style={{ width: 4, height: 4, borderRadius: "50%", flexShrink: 0, background: C.silver, boxShadow: `0 0 8px ${C.silver}` }} />}
    </button>
  );
}

function ProfileStrip({ user, userData }) {
  const initial = (user.displayName || user.email || "?")[0].toUpperCase();
  return (
    <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, border: `1px solid ${C.borderHov}`, background: C.silverFaint, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {userData?.photoURL ? <img src={userData.photoURL} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 12, fontWeight: 700, color: C.silverDim }}>{initial}</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.platinumDim, letterSpacing: "0.08em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.displayName || "Gezgin"}</div>
        <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "var(--bone-text-secondary)", letterSpacing: "0.1em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
      </div>
    </div>
  );
}

export default function SidebarFrame({ activeView, onNavigate, isAdmin: isAdminProp }) {
  const { isOpen, openSidebar, closeSidebar } = useSidebarFrame();
  const { user, userData }                         = useAuth();
  const { isAdmin: ctxAdmin, loading }        = useAdmin();
  const navigate                              = useNavigate();
  const adminFlag = isAdminProp !== undefined ? isAdminProp : ctxAdmin;
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeSidebar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeSidebar]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleLogout = async () => { try { await logout(); } catch {} closeSidebar(); navigate("/"); };
  function handleNav(key) { onNavigate(key); closeSidebar(); }

  const navItems = [
    { key: "feed", label: t('sidebar.frame.feed'), sub: t('sidebar.frame.feedSub'),
      icon: <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg> },
    { key: "mine", label: t('sidebar.frame.mine'), sub: t('sidebar.frame.mineSub'),
      icon: <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" /></svg> },
    { key: "new", label: t('sidebar.frame.new'), sub: t('sidebar.frame.newSub'),
      icon: <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg> },
  ];

  // ── Transform inline style'da — CSS class override sorununu engeller ─────
  const drawerStyle = {
    position: "fixed", top: 0, left: 0,
    width: C.SIDEBAR_W, height: "100vh",
    zIndex: 10000,   /* overlay(9998) üstünde, hamburger(10001) altında */
    background: C.bg, borderRight: `1px solid ${C.border}`,
    display: "flex", flexDirection: "column",
    overflowY: "auto", overflowX: "hidden",
    transform: isOpen ? "translateX(0)" : "translateX(-100%)",
    transition: "transform 0.38s cubic-bezier(0.16,1,0.3,1)",
  };

  return (
    <>
      <style>{`
        /* Tüm cihazlarda sidebar overlay — içerik her zaman tam genişlik */
        .sf-main {
          margin-left: 0 !important;
          width: 100% !important;
          padding-top: 64px !important;
        }
        .sf-nav-item {
          opacity: 0;
          animation: sf-slideIn 0.45s cubic-bezier(0.16,1,0.3,1) forwards;
        }
        @keyframes sf-slideIn {
          from { opacity: 0; transform: translateX(-10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* ── Hamburger — sidebar kapalıyken her cihazda görünür ── */}
      <button
        onClick={openSidebar}
        aria-label={t('sidebar.frame.openMenu')}
        style={{
          display: isOpen ? "none" : "flex",
          position: "fixed", top: 14, left: 14,
          zIndex: 10001,
          width: 40, height: 40,
          background: C.bg, border: `1px solid ${C.border}`,
          color: C.silverDim, cursor: "pointer",
          alignItems: "center", justifyContent: "center",
          fontSize: 18, lineHeight: 1,
          transition: "color 0.2s, border-color 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.silver; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = C.silverDim; }}
      >
        ☰
      </button>

      {/* ── Overlay: sidebar açıkken arkayı karartır ── */}
      {isOpen && (
        <div
          onClick={closeSidebar}
          aria-hidden="true"
          style={{
            position: "fixed", inset: 0,
            zIndex: 9998,   /* drawer(10000) altında */
            background: "rgba(3,3,10,0.75)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        />
      )}

      {/* ── Sidebar drawer ── */}
      <aside className="sf-drawer" style={drawerStyle}>
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>

          <div style={{ position: "absolute", top: 0, right: 0, width: 1, height: "100%", background: "linear-gradient(to bottom,transparent,rgba(192,204,224,0.18) 20%,rgba(192,204,224,0.18) 80%,transparent)", pointerEvents: "none", zIndex: 1 }} />

          {/* Başlık */}
          <div style={{ padding: "20px 20px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg viewBox="0 0 24 24" fill="none" width={22} height={22} stroke="rgba(192,204,224,0.55)" strokeWidth={1.5}>
                <rect x="2" y="6" width="20" height="12" rx="2" />
                <path d="M7 6V18M17 6V18M2 10H7M17 10H22M2 14H7M17 14H22" strokeLinecap="round" />
              </svg>
              <div>
                <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 700, fontStyle: "italic", color: C.silverDim, letterSpacing: "0.08em", textShadow: "0 0 20px rgba(192,204,224,0.3)", display: "block" }}>BoneFrame</span>
                <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--bone-text-muted)" }}>Cinema Library</span>
              </div>
            </div>
            <button onClick={closeSidebar} aria-label="Menüyü kapat"
              style={{ width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: `1px solid ${C.border}`, cursor: "pointer", color: "rgba(192,204,224,0.4)", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.silverDim; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = "rgba(192,204,224,0.4)"; }}
            >
              <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, overflowY: "auto", paddingBottom: 8 }}>
            <div style={{ padding: "14px 20px 8px" }}>
              <button onClick={() => { closeSidebar(); navigate("/"); }}
                style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "var(--bone-text-accent)", fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", padding: 0, transition: "color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.color = C.silverDim}
                onMouseLeave={e => e.currentTarget.style.color = "var(--bone-text-accent)"}
              >← {t('common.back')}</button>
            </div>
            <SilverRule opacity={0.08} />
            <div style={{ padding: "10px 20px 6px" }}>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "var(--bone-text-muted)", textTransform: "uppercase", letterSpacing: "0.2em" }}>✦ BoneFrame ✦</span>
            </div>
            {navItems.map((item, idx) => (
              <div key={item.key} className="sf-nav-item" style={{ animationDelay: `${0.08 + idx * 0.07}s` }}>
                <NavItem itemKey={item.key} activeView={activeView} label={item.label} sub={item.sub} icon={item.icon} onClick={handleNav} />
              </div>
            ))}
            {!loading && adminFlag && (
              <React.Fragment>
                <SilverRule opacity={0.08} />
                <div style={{ padding: "10px 20px 4px" }}>
                  <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(224,92,92,0.4)", textTransform: "uppercase", letterSpacing: "0.2em" }}>✦ Admin ✦</span>
                </div>
                <div className="sf-nav-item" style={{ animationDelay: "0.35s" }}>
                  <NavItem itemKey="moderation" activeView={activeView} label={t('sidebar.modPanel')} sub={t('sidebar.modSub')} onClick={handleNav}
                    icon={<svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93-2.67-1.14-5-4.43-5-7.93V7.18L12 5z" /></svg>}
                  />
                </div>
              </React.Fragment>
            )}
          </nav>

          {user && (<><SilverRule opacity={0.1} /><ProfileStrip user={user} userData={userData} /></>)}

          <div style={{ borderTop: `1px solid ${C.border}`, flexShrink: 0, padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 7 }}>
                <a href="https://www.instagram.com/bonecastofficial?igsh=MWJvYWdkc3E2bWE4bg==" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: `1px solid ${C.border}`, borderRadius: 6, color: "var(--bone-text-secondary)", textDecoration: "none" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
                </a>
                <a href="https://x.com/BoneCastOfcl" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: `1px solid ${C.border}`, borderRadius: 6, color: "var(--bone-text-secondary)", textDecoration: "none" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                </a>
              </div>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "var(--bone-text-muted)", letterSpacing: "0.15em" }}>v1.0.4 · Frame</span>
            </div>
            {user && (
              <button onClick={handleLogout} style={{ width: "100%", background: "transparent", border: "1px solid rgba(192,204,224,0.4)", borderRadius: 8, color: "var(--bone-text-secondary)", padding: "9px 0", fontFamily: "'Courier Prime',monospace", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(200,70,80,0.4)"; e.currentTarget.style.color = "rgba(230,80,100,0.8)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(192,204,224,0.4)"; e.currentTarget.style.color = "var(--bone-text-secondary)"; }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width={11} height={11}><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" /></svg>
                {t('auth.logout')}
              </button>
            )}
            <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, color: "var(--bone-text-muted)", letterSpacing: "0.12em", lineHeight: 1.6, textAlign: "center" }}>{t('sidebar.copyright')}</div>
          </div>

        </div>
      </aside>
    </>
  );
}
